import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  useFindMany, 
  useUser, 
  useAction 
} from '@gadgetinc/react';
import { 
  Box, 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Td, 
  Button, 
  Input, 
  Select, 
  Badge, 
  IconButton,
  Flex,
  Heading,
  Text,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  InputGroup,
  InputLeftElement,
  HStack,
  VStack,
  Card,
  CardBody,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Textarea,
  useDisclosure
} from '@chakra-ui/react';
import { 
  SearchIcon, 
  AddIcon, 
  LockIcon, 
  UnlockIcon, 
  EditIcon,
  ChevronDownIcon,
  QuestionIcon,
  HamburgerIcon
} from '@chakra-ui/icons';
import { MdArrowBack } from 'react-icons/md';
import { api } from '../api';
import { hasRole, normalizeRoles } from '../lib/roleUtils';

const Shifts = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const user = useUser();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('shiftDate');
  const [sortOrder, setSortOrder] = useState('Descending');
  const [selectedShift, setSelectedShift] = useState(null);
  const [unlockReason, setUnlockReason] = useState('');
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [{ data: updateResult, fetching: isUpdating }, updateShift] = useAction(api.shift.update);

  // Determine user role and build filter
  const { filter, userRole } = useMemo(() => {
    if (!user) return { filter: {}, userRole: 'student' };
    
    const roles = user.roles || [];
    const institutionRole = user.institutionRole || 'student';
    
    let role = 'student';
    let shiftFilter = {};
    
    if (hasRole(user, 'admin') || institutionRole === 'administrator') {
      role = 'admin';
      shiftFilter = {
        institution: { equals: user.institution }
      };
    } else if (institutionRole === 'instructor') {
      role = 'instructor';
      shiftFilter = {
        cohort: { equals: user.cohort }
      };
    } else {
      role = 'student';
      shiftFilter = {
        student: {
          id: { equals: user.id }
        }
      };
    }
    
    // Add status filter
    if (statusFilter) {
      shiftFilter.status = { equals: statusFilter };
    }
    
    return { filter: shiftFilter, userRole: role };
  }, [user, statusFilter]);

  const [{ data: shifts, fetching, error }, refetch] = useFindMany(api.shift, {
    filter,
    sort: { [sortBy]: sortOrder },
    first: 250,
    select: {
      id: true,
      shiftDate: true,
      location: true,
      unit: true,
      shiftType: true,
      status: true,
      actualPatients: true,
      locked: true,
      institution: true,
      cohort: { id: true, calendarMode: true, displayName: true },
      student: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
      },
      preceptor: {
        id: true,
        firstName: true,
        lastName: true,
      },
      createdAt: true,
      updatedAt: true,
    },
  });

  // Filter shifts based on search term
  const filteredShifts = useMemo(() => {
    if (!shifts || !searchTerm) return shifts;
    
    return shifts.filter(shift => 
      shift.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.unit?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.student?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.student?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.student?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.institution?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.cohort?.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [shifts, searchTerm]);

  // Check if any visible shifts belong to a sync-mode cohort
  const hasSyncModeCohort = useMemo(() => {
    if (!shifts) return false;
    return shifts.some(s => s.cohort?.calendarMode === "sync");
  }, [shifts]);

  const handleCreateShift = () => {
    navigate('/shifts/new');
  };

  const handleEditShift = (shift) => {
    if (!canEditShift(shift)) {
      toast({
        title: 'Cannot edit shift',
        description: 'This shift is locked or you do not have permission to edit it.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    navigate(`/shifts/${shift.id}/edit`);
  };

  const handleToggleLock = async (shift) => {
    const lockState = getShiftLockState(shift);
    
    // If student is trying to unlock a locked shift, show request modal
    if (userRole === 'student' && lockState.isLocked && getShiftPhase(shift.shiftDate) !== 'active') {
      setSelectedShift(shift);
      setUnlockReason('');
      onOpen();
      return;
    }
    
    // If student is trying to lock their own shift, show confirmation
    if (userRole === 'student' && !lockState.isLocked && shift.student && shift.student.id === user.id) {
      const confirmed = window.confirm(
        'Are you sure you want to lock this shift? Once locked, you will need to request unlock permission from your instructor to make changes.'
      );
      if (!confirmed) return;
    }
    
    if (!lockState.canToggle) {
      toast({
        title: 'Cannot toggle lock',
        description: lockState.reason,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await updateShift({
        id: shift.id,
        locked: !lockState.isLocked,
      });
      
      toast({
        title: lockState.isLocked ? 'Shift unlocked' : 'Shift locked',
        description: `Shift has been ${lockState.isLocked ? 'unlocked' : 'locked'} successfully.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update shift lock status.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'blue';
      case 'in_progress':
        return 'yellow';
      case 'completed':
        return 'green';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getShiftPhase = (shiftDate) => {
    if (!shiftDate) return 'future';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const shift = new Date(shiftDate);
    shift.setHours(0, 0, 0, 0);
    
    if (shift > today) return 'future';
    if (shift.getTime() === today.getTime()) return 'active';
    return 'past';
  };

  const getShiftLockState = (shift) => {
    const phase = getShiftPhase(shift.shiftDate);
    
    // Pre-shift: can be locked/unlocked by instructors
    if (phase === 'future') {
      return {
        isLocked: shift.locked,
        canToggle: userRole !== 'student',
        reason: shift.locked ? 'Shift is locked by instructor' : 'Shift is unlocked'
      };
    }
    
    // Active shift: should be unlocked for data entry
    if (phase === 'active') {
      return {
        isLocked: false,
        canToggle: userRole !== 'student',
        reason: 'Active shift - unlocked for data entry'
      };
    }
    
    // Past shift: instructors/schedulers/admins can still toggle, students cannot
    return {
      isLocked: true,
      canToggle: userRole !== 'student',
      reason: userRole === 'student' ? 'Past shift - auto-locked to preserve data' : 'Past shift - can be unlocked by instructor/admin'
    };
  };

  const canAddPatientEncounter = (shift) => {
    const phase = getShiftPhase(shift.shiftDate);
    const lockState = getShiftLockState(shift);
    
    // Don't allow if locked (except for active shifts)
    if (lockState.isLocked && phase !== 'active') return false;
    
    // Students can only add to their own shifts
    if (userRole === 'student') {
      return shift.student && shift.student.id === user.id;
    }
    
    // Instructors and admins can add to any shift
    return true;
  };

  const canViewPatientList = (shift) => {
    return shift.actualPatients > 0;
  };

  const canEditShift = (shift) => {
    const lockState = getShiftLockState(shift);
    
    // Students can't edit locked shifts
    if (userRole === 'student' && lockState.isLocked) return false;
    
    // Admins can edit any shift
    if (userRole === 'admin') return true;
    
    // Instructors can edit if not auto-locked (past shifts)
    return lockState.canToggle;
  };

  const handleAddPatientEncounter = (shift) => {
    navigate(`/patient-encounters?shiftId=${shift.id}`);
  };

  const handleViewPatientList = (shift) => {
    navigate(`/shifts/${shift.id}/patients`);
  };

  const handleRequestEdit = (shift) => {
    // For now, just navigate to edit. In the future, this could trigger a request flow
    navigate(`/shifts/${shift.id}/edit`);
  };

  const handleSubmitUnlockRequest = async () => {
    if (!unlockReason.trim()) {
      toast({
        title: 'Reason required',
        description: 'Please provide a reason for the unlock request.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // For now, we'll just show a success message
      // In the future, this would call an API to create a shift unlock request
      toast({
        title: 'Unlock request submitted',
        description: 'Your unlock request has been submitted to your instructor for review.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      onClose();
      setSelectedShift(null);
      setUnlockReason('');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit unlock request. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCloseModal = () => {
    onClose();
    setSelectedShift(null);
    setUnlockReason('');
  };

  if (fetching && !shifts) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" color="blue.500" />
        <Text mt={4} color="gray.400">Loading shifts...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={8}>
        <Alert status="error" bg="red.900" color="white">
          <AlertIcon />
          Error loading shifts: {error.toString()}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={8} bg="gray.900" minH="100vh" color="white">
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          <Button
            leftIcon={<MdArrowBack />}
            variant="ghost"
            colorScheme="gray"
            onClick={() => navigate('/')}
            size="md"
          >
            Back to Dashboard
          </Button>
          <Heading size="lg" color="white">
            Shifts Dashboard
          </Heading>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={handleCreateShift}
            size="lg"
            isDisabled={hasSyncModeCohort}
            title={hasSyncModeCohort ? "Shifts are managed by FISDAP calendar sync" : undefined}
          >
            Create New Shift
          </Button>
        </Flex>

        {hasSyncModeCohort && (
          <Alert status="info" borderRadius="md" bg="blue.900" borderColor="blue.600" mb={4}>
            <AlertIcon color="blue.300" />
            <Text fontSize="sm" color="blue.100">
              This cohort uses FISDAP calendar sync. Shifts are managed automatically and cannot be manually created, edited, or deleted.
            </Text>
          </Alert>
        )}

        {/* Filters */}
        <Card bg="gray.800" borderColor="gray.700">
          <CardBody>
            <HStack spacing={4} wrap="wrap">
              <Box minW="300px">
                <InputGroup>
                  <InputLeftElement>
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search shifts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                  />
                </InputGroup>
              </Box>
              
              <Select
                placeholder="All Statuses"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                bg="gray.700"
                border="none"
                color="white"
                w="200px"
              >
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Select>

              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                bg="gray.700"
                border="none"
                color="white"
                w="150px"
              >
                <option value="shiftDate">Date</option>
                <option value="location">Location</option>
                <option value="status">Status</option>
                <option value="createdAt">Created</option>
              </Select>

              <Select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                bg="gray.700"
                border="none"
                color="white"
                w="120px"
              >
                <option value="Descending">Desc</option>
                <option value="Ascending">Asc</option>
              </Select>
            </HStack>
          </CardBody>
        </Card>

        {/* Shifts Table */}
        <Card bg="gray.800" borderColor="gray.700">
          <CardBody p={0}>
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead bg="gray.700">
                  <Tr>
                    <Th color="gray.300" border="none">Date</Th>
                    <Th color="gray.300" border="none">Location</Th>
                    <Th color="gray.300" border="none">Unit</Th>
                    <Th color="gray.300" border="none">Type</Th>
                    <Th color="gray.300" border="none">Status</Th>
                    <Th color="gray.300" border="none">Patients</Th>
                    {userRole !== 'student' && <Th color="gray.300" border="none">Student</Th>}
                    {userRole === 'admin' && <Th color="gray.300" border="none">Institution</Th>}
                    {userRole === 'admin' && <Th color="gray.300" border="none">Cohort</Th>}
                    <Th color="gray.300" border="none">Lock</Th>
                    <Th color="gray.300" border="none">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredShifts?.map((shift) => (
                    <Tr 
                      key={shift.id} 
                      _hover={{ bg: 'gray.700' }}
                    >
                      <Td border="none" color="gray.300">
                        {formatDate(shift.shiftDate)}
                      </Td>
                      <Td border="none" color="gray.300">
                        {shift.location}
                      </Td>
                      <Td border="none" color="gray.300">
                        {shift.unit}
                      </Td>
                      <Td border="none" color="gray.300">
                        {shift.shiftType}
                      </Td>
                      <Td border="none">
                        <Badge 
                          colorScheme={getStatusColor(shift.status)}
                          variant="solid"
                        >
                          {shift.status?.replace('_', ' ')}
                        </Badge>
                      </Td>
                      <Td border="none" color="gray.300">
                        <HStack spacing={2}>
                          <Text>{shift.actualPatients || 0}</Text>
                          {shift.actualPatients > 0 && (
                            <Badge 
                              size="sm" 
                              colorScheme={shift.status === 'completed' ? 'green' : 'yellow'}
                              variant="subtle"
                            >
                              {shift.status === 'completed' ? 'Reports Complete' : 'In Progress'}
                            </Badge>
                          )}
                          {getShiftPhase(shift.shiftDate) === 'active' && (
                            <Badge size="sm" colorScheme="green" variant="outline">
                              Active Shift
                            </Badge>
                          )}
                        </HStack>
                      </Td>
                      {userRole !== 'student' && (
                        <Td border="none" color="gray.300">
                          {shift.student ? 
                            `${shift.student.firstName} ${shift.student.lastName}` : 
                            'Not assigned'
                          }
                        </Td>
                      )}
                      {userRole === 'admin' && (
                        <Td border="none" color="gray.300">
                          {shift.institution}
                        </Td>
                      )}
                      {userRole === 'admin' && (
                        <Td border="none" color="gray.300">
                          {shift.cohort?.displayName || "—"}
                        </Td>
                      )}
                      <Td border="none">
                        {(() => {
                          const lockState = getShiftLockState(shift);
                          const isStudentCanRequest = userRole === 'student' && lockState.isLocked && getShiftPhase(shift.shiftDate) !== 'active';
                          const isStudentOwner = userRole === 'student' && shift.student && shift.student.id === user.id;
                          
                          let tooltipText;
                          if (isStudentCanRequest) {
                            tooltipText = 'Request unlock from instructor';
                          } else if (userRole === 'student' && !lockState.isLocked && isStudentOwner) {
                            tooltipText = 'Lock shift (will require instructor approval to unlock)';
                          } else if (userRole !== 'student') {
                            tooltipText = lockState.isLocked ? 'Unlock shift' : 'Lock shift';
                          } else {
                            tooltipText = lockState.reason;
                          }
                          
                          return (
                            <Tooltip label={tooltipText} hasArrow>
                              <IconButton
                                icon={lockState.isLocked ? <LockIcon /> : <UnlockIcon />}
                                size="sm"
                                colorScheme={lockState.isLocked ? 'red' : 'green'}
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleLock(shift);
                                }}
                                isDisabled={userRole === 'student' ? (!isStudentCanRequest && !isStudentOwner) : false}
                                isLoading={isUpdating}
                                aria-label={lockState.isLocked ? 'Unlock Shift' : 'Lock Shift'}
                              />
                            </Tooltip>
                          );
                        })()}
                      </Td>
                      <Td border="none">
                        <HStack spacing={1}>
                          <Tooltip label="Add Patient Encounter" hasArrow>
                            <IconButton
                              icon={<AddIcon />}
                              size="sm"
                              colorScheme="green"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddPatientEncounter(shift);
                              }}
                              isDisabled={!canAddPatientEncounter(shift)}
                              aria-label="Add Patient Encounter"
                            />
                          </Tooltip>
                          
                          <Tooltip label="View Patient List" hasArrow>
                            <IconButton
                              icon={<HamburgerIcon />}
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewPatientList(shift);
                              }}
                              isDisabled={!canViewPatientList(shift)}
                              aria-label="View Patient List"
                            />
                          </Tooltip>
                          
                          <Tooltip label="Edit Shift / Request Edit" hasArrow>
                            <IconButton
                              icon={
                                <Box position="relative">
                                  <EditIcon />
                                  <QuestionIcon 
                                    position="absolute" 
                                    top="-2px" 
                                    right="-2px" 
                                    boxSize="8px"
                                    color="yellow.400"
                                  />
                                </Box>
                              }
                              size="sm"
                              colorScheme="yellow"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRequestEdit(shift);
                              }}
                              isDisabled={!canEditShift(shift)}
                              aria-label="Edit Shift"
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            
            {filteredShifts?.length === 0 && (
              <Box p={8} textAlign="center">
                <Text color="gray.400" fontSize="lg">
                  No shifts found
                </Text>
                <Text color="gray.500" fontSize="sm" mt={2}>
                  {searchTerm || statusFilter ? 
                    'Try adjusting your search or filters.' : 
                    'Create your first shift to get started.'
                  }
                </Text>
              </Box>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Unlock Request Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="md">
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>Request Shift Unlock</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text color="gray.300">
                You are requesting to unlock the shift scheduled for{' '}
                <Text as="span" fontWeight="bold" color="blue.300">
                  {selectedShift ? formatDate(selectedShift.shiftDate) : ''}
                </Text>
                {' '}at{' '}
                <Text as="span" fontWeight="bold" color="blue.300">
                  {selectedShift ? selectedShift.location : ''}
                </Text>
                .
              </Text>
              
              <Text color="gray.300">
                Please provide a reason for this unlock request:
              </Text>
              
              <Textarea
                placeholder="Enter your reason for requesting to unlock this shift..."
                value={unlockReason}
                onChange={(e) => setUnlockReason(e.target.value)}
                bg="gray.700"
                border="1px solid"
                borderColor="gray.600"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                rows={4}
                resize="vertical"
              />
              
              <Text fontSize="sm" color="gray.400">
                Your instructor will review this request and may approve or deny it.
                You will be notified of their decision.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSubmitUnlockRequest}
              isDisabled={!unlockReason.trim()}
            >
              Submit Request
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Shifts;